import React, { useMemo, useState } from "react"
import { Select } from "antd"

import { Loader } from "shared/ui/Loader/Loader.component"
import { ACCENT } from "constants/brand"
import {
  useInheritedLineageQuery,
  useLineageQuery,
  useQuestionnaireMetadataQuery,
  useSavedQueryNamesQuery,
} from "../api/useLineage"
import { useProjectAuditQuery } from "../api/useProvenanceQueries"
import { LazyLineageView } from "./LazyLineageView.component"

// Sentinels for the DAG a merged file inherits from its sources. Wrapped in underscores
// so they cannot be confused with a saved-query name.
//   __inherited__     -> every source combined
//   __inherited__:N   -> just source N, so you can see what ONE wave contributed
const INHERITED = "__inherited__"
const INHERITED_ONE = "__inherited__:"
const isInherited = (v?: string): boolean => !!v && v.startsWith(INHERITED)

interface ProjectLineageProps {
  projectId: string
}

/**
 * Provenance ▸ Lineage. The derivation graph of a saved query, rebuilt on the server (the data is
 * loaded into a fresh instance and the query run again), so lineage is reachable without a live
 * Data Reporting session. The Reporting console keeps its own live-session view.
 */
const ProjectLineage: React.FC<ProjectLineageProps> = ({ projectId }) => {
  const names = useSavedQueryNamesQuery(projectId)
  const queries = useMemo(() => names.data ?? [], [names.data])
  const [picked, setPicked] = useState<string | undefined>(undefined)

  // A merged file has no saved query of its own, but its derived variables were built by the
  // scripts that ran on each side BEFORE the merge, and those graphs were captured and pinned in
  // the `data.merged` record. So the merged file's lineage is inherited, not recomputable.
  const audit = useProjectAuditQuery(projectId)
  const mergeParents = useMemo(() => {
    const rec = audit.data?.records?.find((r) => r.action === "data.merged")
    return (rec?.parents ?? []).filter((p) => p.lineage?.hash)
  }, [audit.data])
  const hasInherited = mergeParents.length > 0

  // One saved query is the overwhelmingly common case: preselect it. A merged file with no
  // saved query of its own opens on what it inherited.
  const selectedQuery =
    picked ??
    (queries.length === 1
      ? queries[0]
      : hasInherited && names.isSuccess && queries.length === 0
        ? INHERITED
        : undefined)
  const inherited = isInherited(selectedQuery)

  const inheritedHashes = useMemo(() => {
    if (selectedQuery === INHERITED) return mergeParents.map((p) => p.lineage!.hash)
    if (selectedQuery?.startsWith(INHERITED_ONE)) {
      const p = mergeParents[Number(selectedQuery.slice(INHERITED_ONE.length))]
      return p ? [p.lineage!.hash] : []
    }
    return []
  }, [selectedQuery, mergeParents])

  const replayed = useLineageQuery(projectId, inherited ? undefined : selectedQuery)
  const inheritedDag = useInheritedLineageQuery(projectId, inheritedHashes)
  const lineage = inherited ? inheritedDag : replayed
  // Design-side provenance for leaves (advisory: the graph renders without it).
  const qnrMeta = useQuestionnaireMetadataQuery(projectId, !!selectedQuery)

  // Naming a source by its wave is what makes "which wave did this come from?" answerable;
  // fall back to the role, and then the short id, when a merge carried no wave numbers.
  const parentLabel = (p: (typeof mergeParents)[number]): string =>
    p.wave != null ? `Wave ${p.wave} (${p.role})` : `${p.role} · ${p.projectId.slice(0, 8)}…`

  const options = [
    ...(hasInherited
      ? [
          {
            value: INHERITED,
            label: `Inherited from the merge: all ${mergeParents.length} sources`,
          },
          ...mergeParents.map((p, i) => ({
            value: `${INHERITED_ONE}${i}`,
            label: `Inherited from ${parentLabel(p)}`,
          })),
        ]
      : []),
    ...queries.map((q) => ({ value: q, label: q })),
  ]

  const note = (() => {
    if (selectedQuery === INHERITED) {
      return `Captured when the files were merged, from ${mergeParents
        .map(parentLabel)
        .join(" and ")}. The derive scripts ran on each source before the merge, so these variables arrived already built. Shared definitions appear once; anything that differed per wave appears as separate edges.`
    }
    if (selectedQuery?.startsWith(INHERITED_ONE)) {
      const p = mergeParents[Number(selectedQuery.slice(INHERITED_ONE.length))]
      return p ? `Only what ${parentLabel(p)} contributed, as captured at merge time.` : ""
    }
    return "Rebuilt on the server by running the query again. The live session view stays in Data Reporting."
  })()

  return (
    <div className="flex h-full min-h-[480px] flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {hasInherited ? "Lineage" : "Lineage of saved query"}
        </span>
        <Select
          showSearch
          size="small"
          className="min-w-[260px]"
          placeholder={
            hasInherited ? "Pick a source…" : queries.length ? "Pick a saved query…" : "No saved queries"
          }
          options={options}
          value={selectedQuery}
          onChange={(v) => setPicked(v)}
          disabled={options.length === 0}
          optionFilterProp="label"
        />
        <span className="max-w-[560px] text-[11px] text-slate-400">{note}</span>
      </div>

      <div className="min-h-0 flex-1">
        {names.isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader fontSize={36} color={ACCENT} />
          </div>
        ) : names.isError && !hasInherited ? (
          <div className="flex h-full items-center justify-center text-[13px] text-rose-500">
            Couldn’t load the saved queries.
          </div>
        ) : options.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center text-slate-500">
            <p className="text-[14px] font-medium text-slate-600">No saved queries</p>
            <p className="mt-1 max-w-md text-[13px]">
              Lineage traces the transforms a query runs. Save a query in Data Reporting, then
              pick it here to see how its variables derive from the collected data.
            </p>
          </div>
        ) : !selectedQuery ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-[13px] text-slate-500">
            Pick a saved query above to rebuild and trace its lineage.
          </div>
        ) : lineage.isError ? (
          <div className="flex h-full items-center justify-center text-[13px] text-rose-500">
            {inherited
              ? "Couldn’t load the lineage captured at merge time."
              : "Couldn’t rebuild the lineage for this query."}
          </div>
        ) : lineage.data ? (
          <LazyLineageView dag={lineage.data} qnrMeta={qnrMeta.data ?? null} />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Loader fontSize={36} color={ACCENT} />
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjectLineage
